import React from 'react';
import Icon from '@mdi/react';
import {
    mdiCashMultiple,
    mdiContentCut,
    mdiReceiptTextOutline,
    mdiShieldCheckOutline,
    mdiWalletOutline,
} from '@mdi/js';
import { Variables } from '@enter/core';
import { formatCurrency } from '../utils/statement-format.utils';
import type { BalanceBreakdown } from '../balance-breakdown';
import * as S from '../statement-details.styles';

export type StatementBalanceHeroProps = {
    balanceBreakdown: BalanceBreakdown | undefined;
};

export const StatementBalanceHero: React.FC<StatementBalanceHeroProps> = ({ balanceBreakdown }) => {
    const paidUp = !!balanceBreakdown?.paidUp;

    return (
        <S.BalanceCard>
            <S.BalanceHeroMain paidUp={paidUp}>
                <S.BalanceHeroLabel>Current balance</S.BalanceHeroLabel>
                <S.BalanceAmount paidUp={paidUp}>
                    {formatCurrency(String(balanceBreakdown?.currentBalance ?? 0))}
                </S.BalanceAmount>
                <S.BalanceTag paidUp={paidUp}>
                    <S.BalanceTagDot />
                    {paidUp ? 'Paid in full' : 'Balance due'}
                </S.BalanceTag>
                <S.BalanceNote>
                    {paidUp
                        ? 'No outstanding balance on this account.'
                        : `${balanceBreakdown?.openClaimCount ?? 0} claims with an open balance.`}
                </S.BalanceNote>
            </S.BalanceHeroMain>
            <S.BalanceBreakdown>
                <S.BreakdownItem>
                    <S.BreakdownItemHeader>
                        <Icon path={mdiReceiptTextOutline} size={0.65} color={Variables.Colors.n70} />
                        <S.BreakdownItemLabel>Total charged</S.BreakdownItemLabel>
                    </S.BreakdownItemHeader>
                    <S.BreakdownItemValue>
                        {formatCurrency(String(balanceBreakdown?.totalCharged ?? 0))}
                    </S.BreakdownItemValue>
                    <S.BreakdownItemSub>{balanceBreakdown?.claimCount ?? 0} claims</S.BreakdownItemSub>
                </S.BreakdownItem>
                <S.BreakdownItem>
                    <S.BreakdownItemHeader>
                        <Icon path={mdiShieldCheckOutline} size={0.65} color={Variables.Swatches.Green7} />
                        <S.BreakdownItemLabel>Insurance paid</S.BreakdownItemLabel>
                    </S.BreakdownItemHeader>
                    <S.BreakdownItemValue>
                        {formatCurrency(String(balanceBreakdown?.insurancePaid ?? 0))}
                    </S.BreakdownItemValue>
                    <S.BreakdownItemSub>Payer remittance</S.BreakdownItemSub>
                </S.BreakdownItem>
                <S.BreakdownItem>
                    <S.BreakdownItemHeader>
                        <Icon path={mdiContentCut} size={0.65} color={Variables.Colors.n70} />
                        <S.BreakdownItemLabel>Plan adjustments</S.BreakdownItemLabel>
                    </S.BreakdownItemHeader>
                    <S.BreakdownItemValue>
                        {formatCurrency(String(balanceBreakdown?.planAdjustments ?? 0))}
                    </S.BreakdownItemValue>
                    <S.BreakdownItemSub>Contractual write-off</S.BreakdownItemSub>
                </S.BreakdownItem>
                <S.BreakdownItem>
                    <S.BreakdownItemHeader>
                        <Icon path={mdiWalletOutline} size={0.65} color={Variables.Swatches.Orange7} />
                        <S.BreakdownItemLabel>Patient resp.</S.BreakdownItemLabel>
                    </S.BreakdownItemHeader>
                    <S.BreakdownItemValue>
                        {formatCurrency(String(balanceBreakdown?.patientResponsibility ?? 0))}
                    </S.BreakdownItemValue>
                    <S.BreakdownItemSub>After insurance</S.BreakdownItemSub>
                </S.BreakdownItem>
                <S.BreakdownItem>
                    <S.BreakdownItemHeader>
                        <Icon path={mdiCashMultiple} size={0.65} color={Variables.Swatches.Green7} />
                        <S.BreakdownItemLabel>Patient paid</S.BreakdownItemLabel>
                    </S.BreakdownItemHeader>
                    <S.BreakdownItemValue>
                        {formatCurrency(String(balanceBreakdown?.patientPaid ?? 0))}
                    </S.BreakdownItemValue>
                    <S.BreakdownItemSub>
                        {balanceBreakdown?.patientPaymentCount ?? 0} payments
                    </S.BreakdownItemSub>
                </S.BreakdownItem>
            </S.BalanceBreakdown>
        </S.BalanceCard>
    );
};
